// src/components/projects/ProductDetailPage.tsx

import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  Page,
  Header,
  Content,
  Progress,
  InfoCard,
  Table,
} from '@backstage/core-components';
import { useApi, identityApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import { Typography, makeStyles, Box, Tabs, Tab, Accordion, AccordionSummary, AccordionDetails, Paper } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import yaml from 'js-yaml';

const useStyles = makeStyles(theme => ({
  infoCard: { marginTop: theme.spacing(2) },
  tabs: {
    marginBottom: theme.spacing(2),
  },
  accordion: {
    width: '100%',
  },
  detailsBox: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  costSummary: {
    marginBottom: theme.spacing(2),
  },
  chartContainer: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
  },
  chartTitle: {
    marginBottom: theme.spacing(2),
  },
}));

type KV = { key: string; value: string };

// Simple line chart component
const WeeklyCostChart: React.FC<{ 
  data: Array<{ weekStart: string; weekEnd: string; cost: string }>;
  currency: string;
}> = ({ data, currency }) => {
  if (!data || data.length === 0) return null;
  
  const width = 800;
  const height = 300;
  const padding = { top: 20, right: 40, bottom: 60, left: 60 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  
  // Parse costs and find min/max for scaling
  const costs = data.map(d => parseFloat(d.cost));
  const maxCost = Math.max(...costs);
  const minCost = Math.min(...costs);
  const costRange = maxCost - minCost || 1;
  
  // Create points for the line
  const points = data.map((d, i) => {
    const x = padding.left + (i / (data.length - 1 || 1)) * chartWidth;
    const y = padding.top + chartHeight - ((parseFloat(d.cost) - minCost) / costRange) * chartHeight;
    const startDate = new Date(d.weekStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endDate = new Date(d.weekEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return { x, y, cost: d.cost, week: `${startDate} - ${endDate}` };
  });
  
  // Create path string for line
  const linePath = points.map((p, i) => 
    `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
  ).join(' ');
  
  // Create area path (fill under line)
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${height - padding.bottom} L ${padding.left} ${height - padding.bottom} Z`;
  
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} style={{ maxWidth: '100%' }}>
      {/* Area under line */}
      <path d={areaPath} fill="#1976d2" fillOpacity="0.1" />
      
      {/* Line */}
      <path d={linePath} fill="none" stroke="#1976d2" strokeWidth="2" />
      
      {/* Points */}
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="4" fill="#1976d2" />
          <title>{`${p.week}: $${p.cost} ${currency}`}</title>
        </g>
      ))}
      
      {/* Y-axis */}
      <line x1={padding.left} y1={padding.top} x2={padding.left} y2={height - padding.bottom} stroke="#ccc" strokeWidth="1" />
      
      {/* X-axis */}
      <line x1={padding.left} y1={height - padding.bottom} x2={width - padding.right} y2={height - padding.bottom} stroke="#ccc" strokeWidth="1" />
      
      {/* Y-axis labels */}
      {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
        const value = minCost + costRange * ratio;
        const y = padding.top + chartHeight - ratio * chartHeight;
        return (
          <g key={i}>
            <line x1={padding.left - 5} y1={y} x2={padding.left} y2={y} stroke="#ccc" strokeWidth="1" />
            <text x={padding.left - 10} y={y + 4} textAnchor="end" fontSize="12" fill="#666">
              ${value.toFixed(2)}
            </text>
          </g>
        );
      })}
      
      {/* X-axis labels */}
      {points.map((p, i) => {
        const weekData = data[i];
        const startDate = new Date(weekData.weekStart);
        const endDate = new Date(weekData.weekEnd);
        const label = `${startDate.getMonth() + 1}/${startDate.getDate()}-${endDate.getMonth() + 1}/${endDate.getDate()}`;
        return (
          <text 
            key={i}
            x={p.x} 
            y={height - padding.bottom + 20} 
            textAnchor="end" 
            fontSize="10" 
            fill="#666"
            transform={`rotate(-45, ${p.x}, ${height - padding.bottom + 20})`}
          >
            {label}
          </text>
        );
      })}
      
      {/* Cost values on points */}
      {points.map((p, i) => (
        <text 
          key={i}
          x={p.x} 
          y={p.y - 10} 
          textAnchor="middle" 
          fontSize="11" 
          fill="#1976d2"
          fontWeight="bold"
        >
          ${p.cost}
        </text>
      ))}
    </svg>
  );
};

function TabPanel(props: {
  children?: React.ReactNode;
  value: number;
  index: number;
}) {
  const { children, value, index } = props;
  return value === index ? <Box p={1}>{children}</Box> : null;
}

export const ProductDetailPage: React.FC = () => {
  const { projectName, productName } = useParams<{ projectName: string; productName: string }>();
  const location = useLocation();
  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const classes = useStyles();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [config, setConfig] = useState<Record<string, any>>({});
  const [tab, setTab] = useState(0);
  const [costs, setCosts] = useState<Record<string, any>>({});
  const [costsLoading, setCostsLoading] = useState(false);

  // Check for tab query parameter on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam === 'costs') {
      setTab(1);
    }
  }, [location.search]);

  const tableOptions = { paging: false, search: false, showTitle: false, toolbar: false };
  const tableOptionsNoHeader = { paging: false, search: false, showTitle: false, toolbar: false, header: false };

  useEffect(() => {
    const fetchConfig = async () => {
      setLoading(true);
      try {
        const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
        const { token } = await identityApi.getCredentials();
        const resp = await fetch(`${baseUrl}/aws/params`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: `/backstage/project/${projectName}/${productName}` }),
        });
        if (!resp.ok) throw new Error(`Failed to load config: ${resp.status}`);
        const { value: raw } = await resp.json();
        setConfig(yaml.load(raw) as Record<string, any>);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, [projectName, productName, discoveryApi, identityApi]);

  // Fetch costs when Costs tab is selected
  useEffect(() => {
    if (tab !== 1) return; // Only fetch when on Costs tab (index 1)
    
    const fetchCosts = async () => {
      setCostsLoading(true);
      try {
        const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
        const { token } = await identityApi.getCredentials();
        const resp = await fetch(
          `${baseUrl}/aws/product-costs?productName=${encodeURIComponent(productName)}&projectName=${encodeURIComponent(projectName)}`,
          {
            method: 'GET',
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!resp.ok) throw new Error(`Failed to load costs: ${resp.status}`);
        const data = await resp.json();
        setCosts(data.costs || {});
      } catch (e: any) {
        console.error('Failed to fetch costs:', e);
        setCosts({ error: e.message });
      } finally {
        setCostsLoading(false);
      }
    };
    fetchCosts();
  }, [tab, projectName, productName, discoveryApi, identityApi]);

  if (loading) {
    return (
      <Page themeId="home">
        <Header title={productName} />
        <Content>
          <Progress />
        </Content>
      </Page>
    );
  }

  if (error) {
    return (
      <Page themeId="home">
        <Header title={productName} />
        <Content>
          <Typography color="error">{error}</Typography>
        </Content>
      </Page>
    );
  }

  // Create top-level rows
  const mainRows = Object.entries(config)
    .filter(([k]) => !['region', 'components', 'apps', 'application_name'].includes(k))
    .map(([key, value]) => ({ key, value: String(value) }));

  // Format region data
  const regionRows = config.region ? 
    Object.entries(config.region).map(([env, region]) => ({ 
      key: env, 
      value: String(region) 
    })) : [];

  // Format components data
  const componentRows = config.components ? 
    Object.entries(config.components).map(([component, value]) => ({ 
      key: component, 
      value: String(value) 
    })) : [];

  // Format apps data
  const appsRows = Array.isArray(config.apps) ? 
    config.apps.map((app: string | number) => ({ 
      key: String(app), 
      value: '' 
    })) : [];

  return (
    <Page themeId="home">
      <Header title={`${projectName} / ${productName}`} />
      <Content>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          className={classes.tabs}
        >
          <Tab label="Details" />
          <Tab label="Costs" />
        </Tabs>

        <TabPanel value={tab} index={0}>
          <InfoCard className={classes.infoCard}>
            <Table<KV>
              options={tableOptions}
              columns={[
                { title: 'Property', field: 'key' },
                { title: 'Value', field: 'value' },
              ]}
              data={mainRows}
            />

            {regionRows.length > 0 && (
              <Box mt={3}>
                <Typography variant="h6" gutterBottom>
                  Region
                </Typography>
                <Table<KV>
                  options={tableOptionsNoHeader}
                  columns={[
                    { title: '', field: 'key' },
                    { title: '', field: 'value' },
                  ]}
                  data={regionRows}
                />
              </Box>
            )}

            {componentRows.length > 0 && (
              <Box mt={3}>
                <Typography variant="h6" gutterBottom>
                  Components
                </Typography>
                <Table<KV>
                  options={tableOptionsNoHeader}
                  columns={[
                    { title: '', field: 'key' },
                    { title: '', field: 'value' },
                  ]}
                  data={componentRows}
                />
              </Box>
            )}

            {appsRows.length > 0 && (
              <Box mt={3}>
                <Typography variant="h6" gutterBottom>
                  Apps
                </Typography>
                <Table<KV>
                  options={tableOptionsNoHeader}
                  columns={[
                    { title: '', field: 'key' },
                    { title: '', field: 'value' },
                  ]}
                  data={appsRows}
                />
              </Box>
            )}
          </InfoCard>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          {costsLoading ? (
            <Progress />
          ) : costs.error ? (
            <InfoCard>
              <Typography color="error">Error loading costs: {costs.error}</Typography>
            </InfoCard>
          ) : (
            <div className={classes.accordion}>
              {Object.entries(costs).map(([envName, envCost]: [string, any]) => (
                <Accordion key={envName}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">
                      {envName}
                      {envCost.totalCost && (
                        <>
                          <span style={{ marginLeft: '16px', color: '#1976d2', fontWeight: 'bold' }}>
                            ${envCost.totalCost} USD
                          </span>
                          {envCost.changeDescription && envCost.changeDescription !== 'N/A' && (
                            <span style={{ 
                              marginLeft: '12px', 
                              fontSize: '0.9em',
                              color: parseFloat(envCost.percentageChange) >= 0 ? '#d32f2f' : '#388e3c',
                              fontWeight: 'bold'
                            }}>
                              ({envCost.changeDescription} vs last week)
                            </span>
                          )}
                        </>
                      )}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.detailsBox}>
                    {envCost.error ? (
                      <Typography color="error">Error: {envCost.error}</Typography>
                    ) : (
                      <>
                        <Box className={classes.costSummary}>
                          <Typography variant="body1">
                            <strong>Account ID:</strong> {envCost.accountId}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Period:</strong> {envCost.period}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Total Cost:</strong> ${envCost.totalCost} {envCost.currency}
                          </Typography>
                          {envCost.changeDescription && (
                            <Typography 
                              variant="body1" 
                              style={{ 
                                color: parseFloat(envCost.percentageChange) >= 0 ? '#d32f2f' : '#388e3c',
                                fontWeight: 'bold'
                              }}
                            >
                              <strong>Week-over-Week Change:</strong> {envCost.changeDescription}
                            </Typography>
                          )}
                        </Box>
                        
                        {envCost.weeklyCosts && envCost.weeklyCosts.length > 0 && (
                          <>
                            <Paper className={classes.chartContainer} elevation={2}>
                              <Typography variant="h6" className={classes.chartTitle}>
                                Weekly Cost Trend
                              </Typography>
                              <WeeklyCostChart 
                                data={envCost.weeklyCosts} 
                                currency={envCost.currency || 'USD'}
                              />
                            </Paper>
                            
                            <Box mt={2}>
                              <Typography variant="h6" gutterBottom>
                                Weekly Breakdown
                              </Typography>
                              <Table<{ week: string; cost: string; days: string }>
                                options={{ paging: false, search: false, showTitle: false }}
                                columns={[
                                  { title: 'Week Period', field: 'week' },
                                  { title: 'Days', field: 'days', width: '80px' },
                                  { title: 'Cost (USD)', field: 'cost', type: 'numeric' },
                                ]}
                                data={envCost.weeklyCosts.map((w: any) => {
                                  const startDate = new Date(w.weekStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                                  const endDate = new Date(w.weekEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                                  return {
                                    week: `${startDate} - ${endDate}`,
                                    days: w.dayCount ? `${w.dayCount} day${w.dayCount !== 1 ? 's' : ''}` : '-',
                                    cost: `$${w.cost}`,
                                  };
                                }).reverse()}
                              />
                            </Box>
                          </>
                        )}
                      </>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
              {Object.keys(costs).length === 0 && (
                <InfoCard>
                  <Typography>No cost data available for this product.</Typography>
                </InfoCard>
              )}
            </div>
          )}
        </TabPanel>
      </Content>
    </Page>
  );
};
